import type { RecordView } from '@kit/sync/types/record-view';
import { Check } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from '@/shared/i18n/use-translation';
import { formatDate } from '@/shared/lib/dates/format-date';
import { Button } from '@/shared/ui/button/button';
import { completeTask } from '../api/complete-task';
import { patchTask } from '../api/patch-task';
import type { Task } from '../schemas/task';

type TaskDoneButtonProps = {
  readonly view: RecordView<Task>;
};

/** An empty circle that fills with a check: the same for one-off tasks and periodic checks. */
export function TaskDoneButton({ view }: TaskDoneButtonProps) {
  const { t } = useTranslation();
  async function complete() {
    const before = view.data;
    const patch = await completeTask(view);
    // Undo puts back exactly the fields completing changed.
    const undo = Object.fromEntries(
      Object.keys(patch).map((key) => [key, before[key as keyof Task] ?? null]),
    ) as Partial<Task>;
    toast.success(
      patch.status === 'todo'
        ? t('Done. Next check: {date}', { date: formatDate(patch.due_on) })
        : t('Task done'),
      { action: { label: t('Undo'), onClick: () => void patchTask(view.id, undo) } },
    );
  }

  return (
    <Button
      variant="outline"
      size="icon"
      className="group/done size-9 rounded-full border-2 border-muted-foreground/35 hover:border-primary hover:bg-primary/10"
      aria-label={t('Mark “{title}” as done', { title: view.data.title })}
      onClick={() => void complete()}
    >
      <Check className="text-primary opacity-0 transition-opacity group-hover/done:opacity-100 group-focus-visible/done:opacity-100" />
    </Button>
  );
}
